import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Button, Snackbar, IconButton } from '@mui/material';
import { Story } from "../types";
import { decode } from '../../utils/Utils';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  open: boolean;
  onClose: () => void;
  hostageId: string;
  story?: Story;
};

const StoryForm = ({ open, onClose, hostageId, story }: Props) => {
  const { t } = useTranslation();
  const [photo, setPhoto] = useState<string | null>(null);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const [heDescription, setHeDescription] = useState(story?.he?.description || '');
  const [enDescription, setEnDescription] = useState(story?.en?.description || '');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const base64Image = uploadedPhoto ? await getBase64Photo() : story?.photo || null;

    const storyData: Story = {
      hostage: hostageId,
      description: heDescription,
      photo: base64Image,
      isPinned: false,
      reactionsSummary:
        {
          reactionsCount: 0,
          isSelected: false, 
        },
      he:
        {
          description: heDescription
        },
      en:
        {
          description: enDescription
        }
    };

    if (story?.id) {
      await updateStory(story.id, storyData);
    } else {
      await createStory(storyData);
    }
  };

  async function createStory(storyData: Story) {
    try {
      await fetch('/admin/story', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HOSTAGE-ID': storyData.hostage
        },
        body: JSON.stringify(storyData),
      });
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 4000);
    } catch (err) {
      console.error('Error creating story:', err);
    }
  }

  async function updateStory(id: string, storyData: Story) {
    try {
      await fetch(`/admin/story/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-HOSTAGE-ID': storyData.hostage
        },
        body: JSON.stringify(storyData),
      });
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 4000);
    } catch (err) {
      console.error('Error updating story:', err);
    }
  }

  async function getBase64Photo() {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        resolve(event.target?.result?.toString() || '');
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(uploadedPhoto!);
    });
  }

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ style: { borderRadius: '10px 10px 0 0' } }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        style={{ position: 'absolute', right: 16, top: 16 }}  // Position the X button
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit}>
        <h2>{story ? decode(t('createStory.editTitle')) : decode(t('createStory.title'))}</h2>
        <div>
          <label htmlFor="uploadedPhoto">{t('createStory.addImage')}</label>
          <input type="file" accept="image/*,video/*" onChange={(e) => setUploadedPhoto(e.target.files?.[0] || null)} />
        </div>

        <div>
          <label htmlFor="heDescription">תיאור בעברית</label>
          <textarea
            required
            id="heDescription"
            value={heDescription}
            onChange={(e) => setHeDescription(e.target.value)}
            placeholder={t('createStory.storyDescriptionPlaceholder')}
          />
          <label htmlFor="enDescription">English description</label>
          <textarea
            required
            id="enDescription"
            value={enDescription}
            onChange={(e) => setEnDescription(e.target.value)}
            placeholder={t('createStory.storyDescriptionPlaceholder')}
          />
        </div>

        <Button className="submit-button" type="submit">
          {t('createStory.submit')}
        </Button>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={story ? t('createStory.successMessage') : t('createStory.successMessage')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Drawer>
  );
};

export default StoryForm;
